// libs
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import get from 'lodash/get';
import { log } from 'shared/helpers/newRelic';
import { useDispatch } from 'react-redux';

// components
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// api
import { getWishlistItems } from 'shared/api/wishlist';

// helpers
import { getRedirectionURLWithReferer } from 'shared/components/OTPVerification/helpers';
import { transformProducts } from './helpers';
import WishlistContext from './context';
import * as Analytics from 'shared/layouts/DetailsPage/analytics';

// constants
import { DEVICE_TYPE, getDeviceType } from 'shared/helpers/app';
import { AUTH_URI } from 'shared/constants/uri';
import { BREAKPOINTS } from 'shared/styles/base';
import { SIZE_OF_WISHLIST_TO_BE_FETCHED, WISHLIST } from 'shared/layouts/Wishlist/Mobile/constants';
import { TAG_IDENTIFIER } from 'shared/helpers/SentryLogger/constants';

//services
import { addToCartV2 } from 'shared/api/cart';

//actions
import { updateAppCartCount } from 'shared/store/app/actions';
import { incrementCartCount } from 'shared/components/CartMerge';

//constants
import { TEXTS } from './constants';
import { setCartToast } from 'shared/store/cart/actions';
import { REMOVE_PRODUCT_FROM_WISHLIST_FAILED } from 'shared/components/Wishlist/constants';

//contexts
import { ALIGNMENT_TYPES, TOASTER_TYPES, useToastContext } from 'shared/components/Toast';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { ICartAddPartial } from 'shared/api/cart/types';

// types
import { IApi } from './types';

function API({
  children,
  addProductsToWishlistStore,
  pages,
  isUserFetching,
  isUserLoggedIn,
  addSingleProductToWishlistStore,
  totalProducts,
  firstName,
  wishlistConfig,
  resetWishlistTotalCountProductsAndPages,
  removeFromWishlist,
  setCartNudge,
  cartNudgeCount,
}: IApi) {
  const [page, setPage] = useState(1);
  const { search } = useLocation();
  const history = useHistory();
  const [initialAPICalled, setInitialAPICalled] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const dispatch = useDispatch();
  const [isError, setIsError] = useState(false);
  const [errorStatusCode, setErrorStatusCode] = useState('');

  const { showPrice = true, showActionButton = true, wishlistName = 'Wishlist' } = wishlistConfig;

  const isMobile = getDeviceType() === 'MSITE';

  const { mobile = false, desktop = false } = useRemoteConfig('priceReveal');

  const isPriceRevealEnabled = isMobile ? mobile : desktop;

  const [allWishlistedProductsFetched, setAllWishlistedProductsFetched] = useState(false);

  // this state is to check if getWishlistItems has executed or not
  const [isFetching, setIsFetching] = useState(true);

  // dweb support
  const { deviceWidth } = useResponsiveContext();
  const isDesktop = deviceWidth >= BREAKPOINTS.DESKTOP;

  const deviceType = isDesktop ? DEVICE_TYPE.DESKTOP : DEVICE_TYPE.MOBILE;
  const { showToast } = useToastContext();
  const incrementPage = useCallback(() => {
    if (isFetching || allWishlistedProductsFetched) return;

    // page would never get incremented more than its maximum possible value
    if (page > Math.ceil(totalProducts / SIZE_OF_WISHLIST_TO_BE_FETCHED)) return;
    setPage((prev) => prev + 1);
  }, [allWishlistedProductsFetched, isFetching, page, totalProducts]);

  const handleToast = useCallback(
    (message: string, closeTimeout: number) => {
      dispatch(setCartToast(message));
      setTimeout(() => dispatch(setCartToast('')), closeTimeout);
    },
    [dispatch]
  );

  const redirectToAuthentication = useCallback(() => {
    const params = new URLSearchParams(search);

    // if user clicked close on signup login page
    if (params.get('actionStatus') === 'cancelled') {
      history.push('/');
      return;
    }

    // dweb support
    if (isDesktop) {
      history.push('/');
      return;
    }

    // redirect url, to wishlist page
    const newUrl = getRedirectionURLWithReferer({
      url: AUTH_URI.LOGIN_OPTIONS,
      redirectUrl: WISHLIST,
    });
    history.push(newUrl);
  }, [history, isDesktop, search]);

  const getProducts = useCallback(
    (pageNumber: number) => {
      if (pages.indexOf(page) !== -1) {
        setIsFetching(false);
        return;
      }

      setIsFetching(true);

      // api call to get wishlist product based on pagination
      getWishlistItems({ pageNumber, size: SIZE_OF_WISHLIST_TO_BE_FETCHED, isPriceRevealEnabled })
        .then((res: any) => {
          // transform wishlist products so they are compatible for IListing Product
          let wishlistProducts: Listing.IProduct[] = transformProducts(res);

          if (wishlistProducts.length < SIZE_OF_WISHLIST_TO_BE_FETCHED) {
            setAllWishlistedProductsFetched(true);
          }

          // add fetched products to redux
          addProductsToWishlistStore(wishlistProducts, get(res, 'data.itemsCount', 0), pageNumber);

          if (!initialAPICalled) {
            setInitialAPICalled(true);
          }
          setIsFetching(false);
        })
        .catch((error) => {
          log({
            errTitle: 'Error in Wishlist API',
            error,
            tags: { [TAG_IDENTIFIER.MODULE]: WISHLIST },
          });
          setErrorStatusCode(error?.statusCode);
          setIsError(true);
          setIsFetching(false);
        });
    },
    [addProductsToWishlistStore, initialAPICalled, isPriceRevealEnabled, page, pages]
  );

  // for authentication
  useEffect(() => {
    // customer api is still fetching customer data
    if (isUserFetching) {
      return;
    }

    // user is not loggedIn
    if (!isUserLoggedIn) {
      setIsFetching(false);
      redirectToAuthentication();
      return;
    }
  }, [isUserFetching, isUserLoggedIn, redirectToAuthentication]);

  // fetch products of 1st page from wishlist api
  useEffect(() => {
    if (!initialAPICalled && isUserLoggedIn) {
      getProducts(1);
    }
  }, [getProducts, initialAPICalled, isUserLoggedIn]);

  // fetch products when page changes
  useEffect(() => {
    if (isUserLoggedIn === false || !initialAPICalled || isFetching) {
      return;
    }

    getProducts(page);
  }, [getProducts, initialAPICalled, isFetching, isUserLoggedIn, page, pages]);

  // This function would remove the product from wishlist and fetch the last product of the page
  // if there are more products remaining in the wishlist
  const removeProductFromWishlist = useCallback(
    (productId: string) => {
      return removeFromWishlist({ itemId: productId })
        .then(() => {
          if (totalProducts <= page * SIZE_OF_WISHLIST_TO_BE_FETCHED) {
            return;
          }

          // api call to fetch last product for current page
          getWishlistItems({
            pageNumber: page * SIZE_OF_WISHLIST_TO_BE_FETCHED,
            size: 1,
            isPriceRevealEnabled,
          }).then((res: any) => {
            let wishlistProducts: Listing.IProduct[] = transformProducts(res);

            if (wishlistProducts.length > 0) {
              // add fetched products to redux
              addSingleProductToWishlistStore(wishlistProducts[0], res.data.itemsCount);
            }
          });
        })
        .catch((error: Error) => {
          // toast for failure
          showToast({
            type: TOASTER_TYPES.ERROR,
            textAlign: ALIGNMENT_TYPES.LEFT,
            message: error?.message || REMOVE_PRODUCT_FROM_WISHLIST_FAILED,
          });
        });
    },
    [
      addSingleProductToWishlistStore,
      isPriceRevealEnabled,
      page,
      removeFromWishlist,
      showToast,
      totalProducts,
    ]
  );

  const addToCart = useCallback(
    (product) => {
      const productId = product.productId;
      try {
        addToCartV2({
          items: [{ productId, quantity: 1 }],
          deviceType,
        })
          .then((response: { message: string; cartPartial: ICartAddPartial }) => {
            if (deviceType === 'WEBSITE') {
              setCartNudge(cartNudgeCount + 1);
            } else handleToast(TEXTS.ADDED_TO_CART, 4000);
            dispatch(updateAppCartCount(response.cartPartial.quantity));
            incrementCartCount();
            removeProductFromWishlist(productId);
          })
          .catch((err) => {
            showToast({
              type: TOASTER_TYPES.ERROR,
              message: err.message || TEXTS.ADD_TO_CART_ERROR,
              statusCode: err?.statusCode,
            });
          });
      } catch (err) {
        showToast({
          statusCode: err?.statusCode,
          type: TOASTER_TYPES.ERROR,
          message: TEXTS.ADD_TO_CART_ERROR,
        });
      }

      Analytics.onAddToCartClick({
        product_id: productId,
        sizeName: TEXTS.NON_CONFIGUREABLE_PRODUCT_SIZE,
        child_product_id: productId,
        productType: product?.type,
        productData: product,
      });
    },
    [
      cartNudgeCount,
      deviceType,
      dispatch,
      handleToast,
      removeProductFromWishlist,
      setCartNudge,
      showToast,
    ]
  );
  useEffect(() => {
    return () => {
      resetWishlistTotalCountProductsAndPages();
    };
  }, [resetWishlistTotalCountProductsAndPages]);

  const values = useMemo(
    () => ({
      page,
      incrementPage,
      isFetching,
      isRemoving,
      setIsRemoving,
      removeProductFromWishlist,
      isError,
      errorStatusCode,
      addToCart,
      firstName,
      showPrice,
      showActionButton,
      wishlistName,
      isUserFetching,
      initialAPICalled,
    }),
    [
      page,
      incrementPage,
      isFetching,
      isRemoving,
      removeProductFromWishlist,
      isError,
      errorStatusCode,
      addToCart,
      firstName,
      showPrice,
      showActionButton,
      wishlistName,
      isUserFetching,
      initialAPICalled,
    ]
  );
  return <WishlistContext.Provider value={values}>{children}</WishlistContext.Provider>;
}

export default memo(API);
